import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { createLogger } from '../utils/logger-ssr-safe';
import { useAuth } from './AuthContext';

const logger = createLogger({ module: 'TutorialContext', level: 'info' });

// Public routes that should not trigger tutorial checks
const PUBLIC_ROUTES = ['', '/', '/landing', '/login'];

interface TutorialSection {
  title: string;
  content: string;
  image?: string;
}

interface PageTutorial {
  id: string;
  page_key: string;
  title: string;
  content: {
    sections: TutorialSection[];
    version: number;
  };
  created_at: string;
  updated_at: string;
}

interface TutorialContextType {
  isOpen: boolean;
  openTutorial: () => void;
  closeTutorial: () => void;
  currentTutorial: PageTutorial | null;
  currentSection: number;
  totalSections: number;
  nextSection: () => void;
  previousSection: () => void;
  goToSection: (index: number) => void;
  loading: boolean;
  error: Error | null;
  hasTutorial: boolean;
  checkTutorialExists: (pageKey: string) => Promise<boolean>;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const { user } = useAuth();

  // Helper to get page key from pathname
  const getPageKey = useCallback((pathname: string): string => {
    // Remove trailing slash if present
    const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

    // Split the path
    const segments = cleanPath.split('/');

    // If it's the root path or empty, return 'home'
    if (segments.length <= 1) {
      return 'home';
    }

    // Otherwise return the first path segment
    return segments[1].toLowerCase();
  }, []);

  const pageKey = getPageKey(pathname);
  const isPublicRoute = PUBLIC_ROUTES.includes(pathname);
  const shouldQuery = !!user && !isPublicRoute;

  // Use Convex for real-time tutorial data
  // existsForPage is a lighter query just to check if tutorial exists
  const tutorialExists = useQuery(
    api.pageTutorials.existsForPage,
    shouldQuery ? { pageKey } : "skip"
  );

  // getByPageKey fetches the full tutorial content
  const tutorialData = useQuery(
    api.pageTutorials.getByPageKey,
    shouldQuery && isOpen ? { pageKey } : "skip"
  );

  // Transform Convex data to match expected PageTutorial format
  const currentTutorial = useMemo((): PageTutorial | null => {
    if (!tutorialData) return null;

    return {
      id: tutorialData.id,
      page_key: tutorialData.pageKey,
      title: tutorialData.title,
      content: tutorialData.content,
      created_at: tutorialData.createdAt,
      updated_at: tutorialData.updatedAt
    };
  }, [tutorialData]);

  // Derive state from Convex queries
  const hasTutorial = tutorialExists ?? false;
  const loading = shouldQuery && isOpen && tutorialData === undefined;
  const error = null; // Convex handles errors internally

  // Reset section when tutorial changes
  useEffect(() => {
    setCurrentSection(0);
  }, [pageKey]);

  // Legacy function for backwards compatibility
  const checkTutorialExists = useCallback(async (pageKey: string): Promise<boolean> => {
    // With Convex, this is handled reactively - just return current state
    logger.debug('checkTutorialExists called (now handled by Convex query)', { pageKey });
    return hasTutorial;
  }, [hasTutorial]);

  const openTutorial = useCallback(() => {
    // Skip opening tutorial for public routes or when user is not authenticated
    if (!user || isPublicRoute) {
      return;
    }

    logger.debug('Opening tutorial for page:', { pageKey });
    setIsOpen(true);
  }, [pageKey, user, isPublicRoute]);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
    setCurrentSection(0);
  }, []);

  const totalSections = useMemo(() => {
    return currentTutorial?.content?.sections?.length || 0;
  }, [currentTutorial]);

  const nextSection = useCallback(() => {
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
    }
  }, [currentSection, totalSections]);

  const previousSection = useCallback(() => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  }, [currentSection]);

  const goToSection = useCallback((index: number) => {
    if (index >= 0 && index < totalSections) {
      setCurrentSection(index);
    }
  }, [totalSections]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isOpen,
    openTutorial,
    closeTutorial,
    currentTutorial,
    currentSection,
    totalSections,
    nextSection,
    previousSection,
    goToSection,
    loading,
    error,
    hasTutorial,
    checkTutorialExists
  }), [
    isOpen,
    openTutorial,
    closeTutorial,
    currentTutorial,
    currentSection,
    totalSections,
    nextSection,
    previousSection,
    goToSection,
    loading,
    error,
    hasTutorial,
    checkTutorialExists
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { TutorialProvider, useTutorial };
export type { TutorialContextType, PageTutorial, TutorialSection }; 